import { BOOKINGS_PATH } from 'shared/consts/paths';
import { BOOKING_ADD_ONS_SEGMENT } from 'hooks/use-booking-add-ons-offer.hook';

interface AddOnsExitPathInput {
  /** Where the add-on step is standing. */
  pathname: string;
  /** Whether the bookings page is drawing its columns — see `useIsPaneLayout`. */
  isPaneLayout: boolean;
  /*
   * RES-1407 — the segment the step returns to when it is not a pane column: `receipt` with the 2-way
   * chat redesign off, `booked` with it on (`useBookingCompletionSegment`). `booked` re-decides: the
   * back arrow finds the offer still standing and shows Booking confirmed again; declining or a refused
   * booking finds none and lands on My bookings with the booking selected. No path reaches the old
   * receipt under the redesign.
   */
  completionSegment?: string;
}

/**
 * RES-1367 — where "away from the add-on step" is, which is NOT always the receipt.
 *
 * ⭐ THE RULE, NOT A TIDY-UP. Every host but one reaches this step from the receipt, so "back to the
 * booking" was spelled as swapping this segment for `receipt` — and that is still right for all of
 * them. The my-bookings pane broke it: there the booking is a COLUMN of the list, the step is pushed
 * as a CHILD of the list's own path, and the list is still mounted underneath holding the very
 * booking the tenant opened. Rewriting the segment sent them to the OLD receipt page for a booking
 * they were already looking at, which is the bug this answers.
 *
 * So in the pane, dropping the segment is the whole move: the list is still standing, still showing
 * the same booking, and the tenant lands back on the card they left.
 *
 * ⚠️ GATED ON THE PANE, NOT ON THE PATH ALONE, and the two are not interchangeable.
 * `/bookings/add-on-request` is also what the receipt's own navigator produces when the receipt is
 * itself nested under the list (`payment-content.tsx` rewrites `receipt` → this segment), and that
 * one does want its receipt back. The paths are identical; only the layout tells them apart.
 *
 * ⚠️ `lastIndexOf`, and the `> 0` guard with it. A pathname that does not carry the segment at all —
 * which the bounce effect can reach on a crafted url — would otherwise `slice(0, -1)` and return the
 * current path minus its last character.
 */
export const addOnsExitPath = ({
  pathname,
  isPaneLayout,
  completionSegment = 'receipt',
}: AddOnsExitPathInput): string => {
  const segmentAt = pathname.lastIndexOf(`/${BOOKING_ADD_ONS_SEGMENT}`);
  const parent = segmentAt > 0 ? pathname.slice(0, segmentAt) : '';

  if (isPaneLayout && parent.endsWith(BOOKINGS_PATH)) {
    return parent;
  }

  return pathname.replace(BOOKING_ADD_ONS_SEGMENT, completionSegment);
};
